import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LoadingScreen } from "@/components/LoadingScreen";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy-loaded: toaster wrapper (removes Radix Toast + Sonner from critical bundle)
const LazyToaster = lazy(() => import("./components/LazyToaster"));

// Lazy-loaded: public routes (not needed on landing page)
const AccessRequest = lazy(() => import("./pages/AccessRequest"));
const Login = lazy(() => import("./pages/Login"));
const RsvpRedirect = lazy(() => import("./pages/RsvpRedirect"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy-loaded: private routes (behind auth)
const PrivateLayout = lazy(() => import("./components/PrivateLayout"));
const ClubLayout = lazy(() => import("./components/ClubLayout"));
const AgendaPage = lazy(() => import("./pages/AgendaPage"));
const MemberEventDetailPage = lazy(() => import("./pages/MemberEventDetailPage"));
const CodigoPage = lazy(() => import("./pages/CodigoPage"));
const EtiquetaPage = lazy(() => import("./pages/EtiquetaPage"));
const LaCasaPage = lazy(() => import("./pages/LaCasaPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const CarteraLayout = lazy(() => import("./components/CarteraLayout"));
const CarteraGuiaPage = lazy(() => import("./pages/cartera/CarteraGuiaPage"));
const CarteraDepositosPage = lazy(() => import("./pages/cartera/CarteraDepositosPage"));
const CarteraRetirosPage = lazy(() => import("./pages/cartera/CarteraRetirosPage"));

// Lazy-loaded: admin routes
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const SolicitudesPage = lazy(() => import("./pages/admin/SolicitudesPage"));
const MiembrosPage = lazy(() => import("./pages/admin/MiembrosPage"));
const MiembroDetailPage = lazy(() => import("./pages/admin/MiembroDetailPage"));
const EventosPage = lazy(() => import("./pages/admin/EventosPage"));
const EventoDetailPage = lazy(() => import("./pages/admin/EventoDetailPage"));
const TesoreriaPage = lazy(() => import("./pages/admin/TesoreriaPage"));
const CreditosPage = lazy(() => import("./pages/admin/CreditosPage"));
const WithdrawalsPage = lazy(() => import("./pages/admin/WithdrawalsPage"));
const TokenOpsPage = lazy(() => import("./pages/admin/TokenOpsPage"));
const OperationsPage = lazy(() => import("./pages/admin/OperationsPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));

const App = () => (
  <ErrorBoundary>
    <TooltipProvider>
      <Suspense fallback={<LoadingScreen />}><LazyToaster /></Suspense>
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/request" element={<AccessRequest />} />
              <Route path="/login" element={<Login />} />
              <Route path="/rsvp" element={<RsvpRedirect />} />

              {/* Private routes */}
              <Route element={<PrivateLayout />}>
                <Route path="/inicio" element={<ErrorBoundary fallback={null}><AgendaPage /></ErrorBoundary>} />
                <Route path="/agenda/:id" element={<ErrorBoundary fallback={null}><MemberEventDetailPage /></ErrorBoundary>} />
                <Route path="/club" element={<ErrorBoundary fallback={null}><ClubLayout /></ErrorBoundary>}>
                  <Route index element={<Navigate to="/club/la-casa" replace />} />
                  <Route path="la-casa" element={<LaCasaPage />} />
                  <Route path="codigo" element={<CodigoPage />} />
                  <Route path="etiqueta" element={<EtiquetaPage />} />
                </Route>
                <Route path="/cartera" element={<ErrorBoundary fallback={null}><CarteraLayout /></ErrorBoundary>}>
                  <Route index element={<Navigate to="/cartera/guia" replace />} />
                  <Route path="guia" element={<CarteraGuiaPage />} />
                  <Route path="depositos" element={<CarteraDepositosPage />} />
                  <Route path="retiros" element={<CarteraRetirosPage />} />
                </Route>
                <Route path="/cuenta" element={<ErrorBoundary fallback={null}><AccountPage /></ErrorBoundary>} />
                {/* Legacy redirects */}
                <Route path="/agenda" element={<Navigate to="/inicio" replace />} />
                <Route path="/codigo" element={<Navigate to="/club/codigo" replace />} />
                <Route path="/etiqueta" element={<Navigate to="/club/etiqueta" replace />} />
                <Route path="/la-casa" element={<Navigate to="/club/la-casa" replace />} />
              </Route>

              {/* Admin routes */}
              <Route element={<ErrorBoundary fallback={null}><AdminLayout /></ErrorBoundary>}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/solicitudes" element={<SolicitudesPage />} />
                <Route path="/admin/miembros" element={<MiembrosPage />} />
                <Route path="/admin/miembros/:id" element={<MiembroDetailPage />} />
                <Route path="/admin/eventos" element={<EventosPage />} />
                <Route path="/admin/eventos/:id" element={<EventoDetailPage />} />
                <Route path="/admin/tesoreria" element={<TesoreriaPage />} />
                <Route path="/admin/creditos" element={<CreditosPage />} />
                <Route path="/admin/retiros" element={<WithdrawalsPage />} />
                <Route path="/admin/token-ops" element={<TokenOpsPage />} />
                <Route path="/admin/operaciones" element={<OperationsPage />} />
                <Route path="/admin/analitica" element={<AnalyticsPage />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </TooltipProvider>
  </ErrorBoundary>
);

export default App;
